import { WaitlistFormAddonOption } from 'actions/waitlists/WaitlistFormApi';
import { MarketplaceSchedule } from '@spike/model';
import { Waitlist } from 'model/Waitlist';
import moment, { Moment } from 'moment-timezone';

export interface WaitlistFormAddon {
    /** Existing waitlist_addon id (edit mode). */
    id?: number;
    addonServiceId: number;
    name: string;
    /** Informative snapshot of one unit (a daycare add-on is charged per day), null when it can't be resolved. */
    price: number | null;
}

export interface WaitlistFormService {
    /** Existing waitlist_service id (edit mode). */
    id?: number;
    serviceId: number;
    name: string;
    businessArea: string;
    /** Informative snapshot of one occurrence (one night / day / hour for boarding and daycare), null when it can't be resolved. */
    price: number | null;
    /** "YYYY-MM-DD", one per occurrence (appointment day, daycare day, boarding night); [] = ASAP. */
    dates: Array<string>;
    /** null = No preference. */
    preferredStaffId: number | null;
    /** Name from the API, shown when the staff is not in the current location staff list. */
    preferredStaffName?: string | null;
    addons: Array<WaitlistFormAddon>;
}

export interface WaitlistFormPet {
    id: number;
    name: string;
    image: string | null;
    type: string;
    services: Array<WaitlistFormService>;
}

export interface WaitlistFormValues {
    marketplaceId?: number;
    customerId?: number;
    pets: Array<WaitlistFormPet>;
    preferredTime: string | null;
    validUntil: Moment | null;
    notes: string;
}

export interface WaitlistAddonInputDto {
    id?: number;
    _destroy?: boolean;
    addon_service_id?: number;
    price?: number | null;
}

export interface WaitlistServiceInputDto {
    id?: number;
    _destroy?: boolean;
    pet_id?: number;
    service_id?: number;
    price?: number | null;
    preferred_staff_id?: number | null;
    dates?: Array<string>;
    waitlist_addons_attributes?: Array<WaitlistAddonInputDto>;
}

export interface WaitlistInputDto {
    customer_id?: number;
    preferred_time: string | null;
    valid_until: string | null;
    notes: string | null;
    waitlist_services_attributes: Array<WaitlistServiceInputDto>;
}

const DATE_FORMAT = 'YYYY-MM-DD';
const SLOT_MINUTES = 15;

// Boarding and daycare are charged per night / day: the snapshot is the total of the line.
const PER_DATE_AREAS = ['boarding', 'daycare'];

export const getWaitlistServiceQuantity = (businessArea: string, dates: Array<string>): number =>
    PER_DATE_AREAS.includes(businessArea) ? Math.max(1, dates.length) : 1;

// Same as Add Appointment: daycare add-ons are charged every day, the rest once per line.
const getWaitlistAddonQuantity = (businessArea: string, dates: Array<string>): number =>
    businessArea === 'daycare' ? Math.max(1, dates.length) : 1;

const toLinePrice = (unitPrice: number | null, quantity: number): number | null =>
    unitPrice === null ? null : Math.round(unitPrice * quantity * 100) / 100;

const toUnitPrice = (linePrice: number | null, quantity: number): number | null =>
    linePrice === null ? null : linePrice / quantity;

export const normalizeWaitlistDates = (dates: Array<string>): Array<string> => Array.from(new Set(dates)).sort();

const sameDates = (a: Array<string>, b: Array<string>) =>
    normalizeWaitlistDates(a).join() === normalizeWaitlistDates(b).join();

/** Boarding nights of a stay: from the check-in to the night before the check-out. */
export const getBoardingNights = (checkIn: Moment, checkOut: Moment): Array<string> => {
    const nights: Array<string> = [];
    const night = checkIn.clone().startOf('day');

    do {
        nights.push(night.format(DATE_FORMAT));
        night.add(1, 'day');
    } while (night.isBefore(checkOut, 'day'));

    return nights;
};

/** Earliest date asked for in any service; null when everything is ASAP. */
export const getFirstWaitlistDate = (pets: Array<WaitlistFormPet>, timeZone: string): Moment | null => {
    const [first] = normalizeWaitlistDates(pets.flatMap(pet => pet.services.flatMap(service => service.dates)));
    return fromWaitlistDate(first ?? null, timeZone);
};

export const toWaitlistDate = (date: Moment | null): string | null => (date ? date.format(DATE_FORMAT) : null);

export const fromWaitlistDate = (date: string | null, timeZone: string): Moment | null =>
    date ? moment.tz(date, DATE_FORMAT, timeZone) : null;

// Dental is booked as part of Grooming, so it shares Grooming add-ons.
const addonAreaFor = (businessArea: string) => (businessArea === 'dental' ? 'grooming' : businessArea);

export const getAddonOptionsForService = (
    service: WaitlistFormService,
    addons: Array<WaitlistFormAddonOption>
): Array<WaitlistFormAddonOption> =>
    addons.filter(
        addon =>
            // Same rule as Add Appointment: an add-on without business areas applies to all of them.
            (addon.businessAreas.length === 0 || addon.businessAreas.includes(addonAreaFor(service.businessArea))) &&
            !service.addons.some(selected => selected.addonServiceId === addon.id)
    );

const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

const toTime = (minutes: number) =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * Preferred Time options: 15-minute steps inside the location business hours.
 * With a specific date it uses that day (special hours first); with ASAP it uses
 * the widest range across the week. The closing time itself is not offered.
 */
export const getWaitlistTimeSlots = (schedule: MarketplaceSchedule | undefined, date: Moment | null): Array<string> => {
    if (!schedule) {
        return [];
    }

    let ranges: Array<{ open: string; close: string }> = [];

    if (date) {
        const specialHour = schedule.specialHours?.find(special => moment(special.date).isSame(date, 'day'));

        if (specialHour?.isClosed) {
            return [];
        }

        const dayHours =
            specialHour?.businessHours ??
            schedule.week[date.format('dddd').toLowerCase() as keyof MarketplaceSchedule['week']];

        ranges = dayHours ? [dayHours] : [];
    } else {
        ranges = Object.values(schedule.week).filter((hours): hours is { open: string; close: string } => !!hours);
    }

    if (ranges.length === 0) {
        return [];
    }

    const open = Math.min(...ranges.map(range => toMinutes(range.open)));
    const close = Math.max(...ranges.map(range => toMinutes(range.close)));
    const first = Math.ceil(open / SLOT_MINUTES) * SLOT_MINUTES;

    const slots: Array<string> = [];

    for (let minutes = first; minutes < close; minutes += SLOT_MINUTES) {
        slots.push(toTime(minutes));
    }

    return slots;
};

export const getWaitlistOpenDates = (
    schedule: MarketplaceSchedule | undefined,
    from: Moment,
    days = 365
): Array<string> => {
    if (!schedule) {
        return [];
    }

    const dates: Array<string> = [];
    const day = from.clone().startOf('day');

    for (let i = 0; i < days; i++, day.add(1, 'day')) {
        if (getWaitlistTimeSlots(schedule, day).length > 0) {
            dates.push(day.format('YYYY-MM-DD'));
        }
    }

    return dates;
};

export const convertWaitlistToFormValues = (waitlist: Waitlist, timeZone: string): WaitlistFormValues => ({
    marketplaceId: waitlist.marketplaceId,
    customerId: waitlist.customerId,
    pets: waitlist.services.reduce<Array<WaitlistFormPet>>((pets, service) => {
        // The form keeps unit prices; the saved snapshot is the total of the line.
        const quantity = getWaitlistServiceQuantity(service.businessArea, service.dates);
        const addonQuantity = getWaitlistAddonQuantity(service.businessArea, service.dates);

        const formService: WaitlistFormService = {
            id: service.id,
            serviceId: service.serviceId,
            name: service.serviceName,
            businessArea: service.businessArea,
            price: toUnitPrice(service.price, quantity),
            dates: normalizeWaitlistDates(service.dates),
            preferredStaffId: service.preferredStaffId,
            preferredStaffName: service.preferredStaffName,
            addons: service.addons.map(addon => ({
                id: addon.id,
                addonServiceId: addon.addonServiceId,
                name: addon.addonServiceName,
                price: toUnitPrice(addon.price, addonQuantity)
            }))
        };

        const pet = pets.find(current => current.id === service.petId);

        if (pet) {
            return pets.map(current =>
                current.id === pet.id ? { ...current, services: [...current.services, formService] } : current
            );
        }

        return [
            ...pets,
            {
                id: service.petId,
                name: service.petName,
                image: service.petImage,
                type: service.petType,
                services: [formService]
            }
        ];
    }, []),
    preferredTime: waitlist.preferredTime,
    validUntil: fromWaitlistDate(waitlist.validUntil, timeZone),
    notes: waitlist.notes ?? ''
});

const buildNewAddonInput = (service: WaitlistFormService, addon: WaitlistFormAddon): WaitlistAddonInputDto => ({
    addon_service_id: addon.addonServiceId,
    price: toLinePrice(addon.price, getWaitlistAddonQuantity(service.businessArea, service.dates))
});

const buildNewServiceInput = (petId: number, service: WaitlistFormService): WaitlistServiceInputDto => ({
    pet_id: petId,
    service_id: service.serviceId,
    price: toLinePrice(service.price, getWaitlistServiceQuantity(service.businessArea, service.dates)),
    preferred_staff_id: service.preferredStaffId,
    dates: normalizeWaitlistDates(service.dates),
    waitlist_addons_attributes: service.addons.map(addon => buildNewAddonInput(service, addon))
});

const buildScalarInput = (values: WaitlistFormValues) => ({
    preferred_time: values.preferredTime,
    valid_until: toWaitlistDate(values.validUntil),
    notes: values.notes.trim().length > 0 ? values.notes.trim() : null
});

export const buildCreateWaitlistInput = (values: WaitlistFormValues): WaitlistInputDto => ({
    customer_id: values.customerId,
    ...buildScalarInput(values),
    waitlist_services_attributes: values.pets.flatMap(pet =>
        pet.services.map(service => buildNewServiceInput(pet.id, service))
    )
});

/**
 * Partial update: only the services and add-ons that changed are sent, with their `id`.
 * Items without `id` are created and items with `_destroy` are removed; the rest stay untouched.
 */
export const buildUpdateWaitlistInput = (original: Waitlist, values: WaitlistFormValues): WaitlistInputDto => {
    const currentServices = values.pets.flatMap(pet => pet.services.map(service => ({ petId: pet.id, service })));

    const removedServices: Array<WaitlistServiceInputDto> = original.services
        .filter(service => !currentServices.some(current => current.service.id === service.id))
        .map(service => ({ id: service.id, _destroy: true }));

    const newServices: Array<WaitlistServiceInputDto> = currentServices
        .filter(({ service }) => service.id === undefined)
        .map(({ petId, service }) => buildNewServiceInput(petId, service));

    const changedServices: Array<WaitlistServiceInputDto> = currentServices
        .filter(({ service }) => service.id !== undefined)
        .map(({ service }) => {
            const originalService = original.services.find(current => current.id === service.id);

            const removedAddons = (originalService?.addons ?? [])
                .filter(addon => !service.addons.some(current => current.id === addon.id))
                .map(addon => ({ id: addon.id, _destroy: true }));

            const serviceChanged = originalService !== undefined && service.serviceId !== originalService.serviceId;
            const staffChanged = service.preferredStaffId !== (originalService?.preferredStaffId ?? null);
            const datesChanged = !sameDates(service.dates, originalService?.dates ?? []);

            // Daycare add-ons are charged per day: new dates change the price of the ones already saved.
            const repricedAddons =
                datesChanged && service.businessArea === 'daycare'
                    ? service.addons
                          .filter(addon => addon.id !== undefined)
                          .map(addon => ({ id: addon.id, price: buildNewAddonInput(service, addon).price }))
                    : [];

            const newAddons = service.addons
                .filter(addon => addon.id === undefined)
                .map(addon => buildNewAddonInput(service, addon));

            const addons = [...removedAddons, ...repricedAddons, ...newAddons];
            const linePrice = toLinePrice(
                service.price,
                getWaitlistServiceQuantity(service.businessArea, service.dates)
            );

            return {
                id: service.id,
                // A different service on the same item keeps its id, with the price of the new service.
                ...(serviceChanged ? { service_id: service.serviceId } : {}),
                ...(serviceChanged || datesChanged ? { price: linePrice } : {}),
                ...(staffChanged ? { preferred_staff_id: service.preferredStaffId } : {}),
                // The dates are replaced as a whole ([] = back to ASAP).
                ...(datesChanged ? { dates: normalizeWaitlistDates(service.dates) } : {}),
                ...(addons.length > 0 ? { waitlist_addons_attributes: addons } : {})
            };
        })
        // Only the services whose service, staff, dates or add-ons changed.
        .filter(service => Object.keys(service).length > 1);

    return {
        ...buildScalarInput(values),
        waitlist_services_attributes: [...removedServices, ...newServices, ...changedServices]
    };
};

const FIELD_LABELS: Record<string, string> = {
    'customer': 'Client',
    'preferred_time': 'Preferred time',
    'valid_until': 'Valid until',
    'notes': 'Notes',
    'waitlist_services.pet': 'Pet',
    'waitlist_services.preferred_staff': 'Preferred staff',
    'waitlist_services.dates': 'Requested dates',
    'waitlist_services.service': 'Service',
    'waitlist_services.waitlist_addons.addon_service': 'Add-on'
};

// Rails validation hash -> readable messages ("Requested dates can't be in the past").
export const formatWaitlistErrors = (errors: Record<string, Array<string>> | undefined): Array<string> => {
    if (!errors || typeof errors !== 'object') {
        return [];
    }

    return Object.entries(errors).flatMap(([field, messages]) => {
        const label = field === 'base' ? '' : FIELD_LABELS[field] ?? field.split('.').pop()?.replace(/_/g, ' ') ?? '';
        return (Array.isArray(messages) ? messages : [String(messages)]).map(message =>
            label ? `${label.charAt(0).toUpperCase()}${label.slice(1)} ${message}` : message
        );
    });
};
